import {
  ContratSignatureValidationPjRelanceGQL,
  DemandeValidationPjRefuserGQL,
  DemandeValidationPjsAffecterGQL,
  DemandeValidationPjSortInput,
  DemandeValidationPjStatutBackOfficeEnum,
  GetDemandeValidationPjsCountByStatutBackOfficeGQL,
  GetDemandeValidationPjsFilteredBackOfficeGQL,
  SortEnumType,
} from '@graphql/generated';
import { DemandeValidationPj as DemandeValidationPjGql } from '@graphql/generated';
import { inject, Injectable } from '@angular/core';
import { Pagination } from '@lib/models/pagination.model';
import { combineLatest, lastValueFrom, map, Observable } from 'rxjs';
import { DemandeValidationPjBackOfficeService } from '../demande-validation-pj-backoffice.service';
import { BackOfficeTabsCounters } from '../../../demandes-affiliation/models/backoffice-tabs-counters.model';
import { DemandeValidationPjStatutBackOfficeMapper } from '@lib/mappers/graphql/demande-validation-pj-statut-backoffice-mapper';
import { DemandeValidationPj } from '@lib/models/demande-validation-pj/demande-validation-pj.model';
import { DemandeValidationPjMapper } from '@lib/mappers/graphql/demande-validation-pj-mapper';
import { PieceJustificativeDocumentTypeMapper } from '@lib/mappers/graphql/piece-justificative-document-type-mapper';
import { PieceJustificativeDocumentType } from '@lib/models/demande-validation-pj/types/piece-justificative-document-types.type';
import { DemandeValidationPjStatutBackOffice } from '@lib/models/demande-validation-pj/types/demande-validation-pj-statut.type';

@Injectable({ providedIn: 'root' })
export class GraphQlDemandeValidationPjBackOfficeService extends DemandeValidationPjBackOfficeService {
  private readonly getDemandeValidationPjsCountGql = inject(
    GetDemandeValidationPjsCountByStatutBackOfficeGQL
  );
  private readonly getDemandeValidationPjsFilteredGql = inject(
    GetDemandeValidationPjsFilteredBackOfficeGQL
  );
  private readonly demandeValidationPjsAffecterGql = inject(
    DemandeValidationPjsAffecterGQL
  );
  private readonly demandeValidationPjRefuserGql = inject(DemandeValidationPjRefuserGQL);
  private readonly contratSignatureValidationPjRelanceGql = inject(
    ContratSignatureValidationPjRelanceGQL
  );

  private readonly aAffecterStatutsGql: DemandeValidationPjStatutBackOfficeEnum[] =
    this.aAffecterStatuts.map(
      s => DemandeValidationPjStatutBackOfficeMapper.toGraphQl(s)!
    );

  private readonly aControlerGql: DemandeValidationPjStatutBackOfficeEnum[] =
    this.aControlerStatuts.map(
      s => DemandeValidationPjStatutBackOfficeMapper.toGraphQl(s)!
    );

  private readonly enAttenteGql: DemandeValidationPjStatutBackOfficeEnum[] =
    this.enAttenteStatuts.map(
      s => DemandeValidationPjStatutBackOfficeMapper.toGraphQl(s)!
    );

  private readonly enSignatureGql: DemandeValidationPjStatutBackOfficeEnum[] =
    this.enSignatureStatuts.map(
      s => DemandeValidationPjStatutBackOfficeMapper.toGraphQl(s)!
    );

  private readonly queryRefAAffecter = this.getDemandeValidationPjsCountGql.watch({
    variables: { statuts: this.aAffecterStatutsGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private readonly queryRefAControler = this.getDemandeValidationPjsCountGql.watch({
    variables: { statuts: this.aControlerGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private readonly queryRefEnAttente = this.getDemandeValidationPjsCountGql.watch({
    variables: { statuts: this.enAttenteGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private readonly queryRefEnSignature = this.getDemandeValidationPjsCountGql.watch({
    variables: { statuts: this.enSignatureGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private aAffecter$ = this.queryRefAAffecter.valueChanges.pipe(
    map(result => result.data?.demandeValidationPjsCountByStatutBackOffice)
  );

  private aControler$ = this.queryRefAControler.valueChanges.pipe(
    map(result => result.data?.demandeValidationPjsCountByStatutBackOffice)
  );

  private enAttente$ = this.queryRefEnAttente.valueChanges.pipe(
    map(result => result.data?.demandeValidationPjsCountByStatutBackOffice)
  );

  private enSignature$ = this.queryRefEnSignature.valueChanges.pipe(
    map(result => result.data?.demandeValidationPjsCountByStatutBackOffice)
  );

  public override watchDemandesCountByCategories(): Observable<BackOfficeTabsCounters> {
    const interval = 15000;

    this.queryRefAAffecter.startPolling(interval);
    this.queryRefAControler.startPolling(interval);
    this.queryRefEnAttente.startPolling(interval);
    this.queryRefEnSignature.startPolling(interval);

    return combineLatest([
      this.aAffecter$,
      this.aControler$,
      this.enAttente$,
      this.enSignature$,
    ]).pipe(
      map(
        ([aAffecter, aControler, enAttente, enSignature]) =>
          ({
            aAffecter: aAffecter,
            aControler: aControler,
            enAttente: enAttente,
            enSignature: enSignature,
          }) as BackOfficeTabsCounters
      )
    );
  }

  private updateCountByCategories(): void {
    this.queryRefAAffecter.refetch();
    this.queryRefAControler.refetch();
    this.queryRefEnAttente.refetch();
    this.queryRefEnSignature.refetch();
  }

  public override async getDemandeValidationPjs(
    search: string | null,
    statuts: DemandeValidationPjStatutBackOffice[],
    gestionnaireCodes: string[],
    pieceJustificativeTypes: PieceJustificativeDocumentType[],
    pageIndex: number,
    pageSize: number,
    sortField: 'dateDeclaration' | 'dateUpdate',
    sortOrder: 'asc' | 'desc'
  ): Promise<Pagination<DemandeValidationPj>> {
    const statutsGql = statuts.map(
      s => DemandeValidationPjStatutBackOfficeMapper.toGraphQl(s)!
    );
    const pieceJustificativeTypeGql = pieceJustificativeTypes.map(p =>
      PieceJustificativeDocumentTypeMapper.toGraphQl(p)
    );

    const sortTypeGql: SortEnumType =
      sortOrder === 'asc' ? SortEnumType.Asc : SortEnumType.Desc;

    const sortGql: DemandeValidationPjSortInput[] =
      sortField === 'dateDeclaration'
        ? [{ dateDeclaration: sortTypeGql }, { dateUpdate: SortEnumType.Desc }]
        : [{ dateUpdate: sortTypeGql }, { dateDeclaration: SortEnumType.Desc }];

    const queryResult = await lastValueFrom(
      this.getDemandeValidationPjsFilteredGql.fetch({
        variables: {
          take: pageSize,
          skip: pageSize * pageIndex,
          search: search,
          statuts: statutsGql,
          gestionnaireCodes: gestionnaireCodes,
          pieceJustificativeTypes: pieceJustificativeTypeGql,
          order: sortGql,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const data = queryResult.data!.demandeValidationPjsFilteredBackOffice;

    const demandeValidationPjs: DemandeValidationPj[] =
      data?.items?.map(i =>
        DemandeValidationPjMapper.fromGraphQL(i as DemandeValidationPjGql, true)
      ) ?? [];

    return new Pagination<DemandeValidationPj>(
      demandeValidationPjs,
      pageIndex,
      pageSize,
      data!.totalCount
    );
  }

  public override async affecterDemandeValidationPj(
    publicIds: string[],
    gestionnaireId: string
  ): Promise<void> {
    await lastValueFrom(
      this.demandeValidationPjsAffecterGql.mutate({
        variables: {
          gestionnaireId: gestionnaireId,
          demandeValidationPjPublicIds: publicIds,
        },
      })
    );
    this.updateCountByCategories();
  }

  public override async refuserDemandeValidationPj(
    publicId: string,
    commentaire: string
  ): Promise<void> {
    await lastValueFrom(
      this.demandeValidationPjRefuserGql.mutate({
        variables: {
          publicId: publicId,
          commentaire: commentaire,
        },
      })
    );
    this.updateCountByCategories();
  }

  public override async relancerSignature(
    publicId: string,
    newEmail: string | null,
    useNewEmailAsDefault: boolean,
    newNumber: string | null,
    useNewNumberAsDefault: boolean
  ): Promise<void> {
    await lastValueFrom(
      this.contratSignatureValidationPjRelanceGql.mutate({
        variables: {
          publicId,
          newEmail,
          useNewEmailAsDefault,
          newNumber,
          useNewNumberAsDefault,
        },
      })
    );
    this.updateCountByCategories();
  }
}
